import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import GetAppIcon from "@mui/icons-material/GetApp";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ACTION_LABELS = {
  login:               { label: "Login",              color: "#27AE60" },
  logout:              { label: "Logout",             color: "#8A9096" },
  ticket_created:      { label: "Ticket criado",      color: "#0071BC" },
  ticket_open:         { label: "Ticket aberto",      color: "#0071BC" },
  ticket_pending:      { label: "Aguardando",         color: "#F4C02F" },
  ticket_closed:       { label: "Ticket fechado",     color: "#555A5F" },
  ticket_transferred:  { label: "Transferido",        color: "#C23582" },
  message_sent:        { label: "Msg enviada",        color: "#F37021" },
  message_sent_media:  { label: "Mídia enviada",      color: "#F37021" },
  contact_created:     { label: "Contato criado",     color: "#273A74" },
  contact_updated:     { label: "Contato editado",    color: "#273A74" },
  contacts_exported:   { label: "Contatos exportados", color: "#27AE60" },
  tag_added:           { label: "Tag adicionada",     color: "#27AE60" },
  tag_removed:         { label: "Tag removida",       color: "#E74C3C" },
  setting_changed:     { label: "Config alterada",    color: "#555A5F" },
  whatsapp_created:    { label: "WhatsApp criado",    color: "#27AE60" },
  whatsapp_deleted:    { label: "WhatsApp removido",  color: "#E74C3C" },
  whatsapp_disconnected: { label: "WA desconectado",  color: "#E74C3C" },
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    padding: theme.spacing(2),
    alignItems: "flex-end",
  },
  filterField: { minWidth: 160 },
  dateField:   { minWidth: 160 },
  tableHead: {
    "& th": { fontWeight: 700, color: "#555A5F" },
  },
  chip: {
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    height: 22,
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#8A9096",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
  },
  liveIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: "#27AE60",
  },
  detailsCell: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "default",
  },
}));

const UserActivity = () => {
  const classes = useStyles();
  const [activities, setActivities] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  const refreshTimer = useRef(null);

  useEffect(() => {
    api.get("/users", { params: { pageNumber: "1" } })
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  const fetchActivities = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { pageNumber: page + 1 };
      if (filterUser)      params.userId    = filterUser;
      if (filterAction)    params.action    = filterAction;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate)   params.endDate   = filterEndDate;

      const { data } = await api.get("/user-activity", { params });
      setActivities(Array.isArray(data?.activities) ? data.activities : []);
      setCount(data?.count ?? 0);
      setLastRefresh(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, filterUser, filterAction, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // auto-refresh every 30s silently
  useEffect(() => {
    refreshTimer.current = setInterval(() => fetchActivities(true), 30000);
    return () => clearInterval(refreshTimer.current);
  }, [fetchActivities]);

  const handleExportCSV = () => {
    const header = ["Data/Hora", "Usuário", "Email", "Ação", "Detalhes", "IP"];
    const rows = (activities || []).map(act => [
      act.createdAt ? new Date(act.createdAt).toLocaleString("pt-BR") : "-",
      act.user?.name || "-",
      act.user?.email || "-",
      ACTION_LABELS[act.action]?.label || act.action,
      act.details ? act.details.replace(/"/g, "'") : "-",
      act.ip || "-",
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(v => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `atividade_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString("pt-BR") : "-";

  const parseDetails = (details) => {
    if (!details) return "-";
    try {
      const obj = JSON.parse(details);
      return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(" | ");
    } catch { return details; }
  };

  const getChip = (action) => {
    const meta = ACTION_LABELS[action] || { label: action, color: "#8A9096" };
    return <Chip label={meta.label} className={classes.chip} style={{ backgroundColor: meta.color }} />;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Atividade de Usuários</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">

        {/* Header with refresh + export */}
        <div className={classes.headerRow}>
          <div className={classes.filters} style={{ padding: "12px 0" }}>
            <FormControl variant="outlined" size="small" className={classes.filterField}>
              <InputLabel>Usuário</InputLabel>
              <Select value={filterUser} onChange={(e) => { setFilterUser(e.target.value); setPage(0); }} label="Usuário">
                <MenuItem value="">Todos</MenuItem>
                {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" className={classes.filterField}>
              <InputLabel>Ação</InputLabel>
              <Select value={filterAction} onChange={(e) => { setFilterAction(e.target.value); setPage(0); }} label="Ação">
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(ACTION_LABELS).map(([key, meta]) => (
                  <MenuItem key={key} value={key}>{meta.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="De" type="date" size="small" variant="outlined"
              className={classes.dateField} InputLabelProps={{ shrink: true }}
              value={filterStartDate}
              onChange={(e) => { setFilterStartDate(e.target.value); setPage(0); }} />

            <TextField label="Até" type="date" size="small" variant="outlined"
              className={classes.dateField} InputLabelProps={{ shrink: true }}
              value={filterEndDate}
              onChange={(e) => { setFilterEndDate(e.target.value); setPage(0); }} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 8 }}>
            {lastRefresh && (
              <span className={classes.liveIndicator}>
                <FiberManualRecordIcon style={{ fontSize: 10 }} />
                ao vivo · {lastRefresh.toLocaleTimeString("pt-BR")}
              </span>
            )}
            <Tooltip title="Atualizar agora">
              <IconButton size="small" onClick={() => fetchActivities()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exportar CSV">
              <Button size="small" variant="outlined" startIcon={<GetAppIcon />}
                onClick={handleExportCSV} style={{ borderColor: "#F37021", color: "#F37021" }}>
                CSV
              </Button>
            </Tooltip>
          </div>
        </div>

        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Detalhes</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={28} style={{ color: "#F37021" }} />
                </TableCell>
              </TableRow>
            ) : !activities || activities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography className={classes.noData}>
                    Nenhuma atividade encontrada.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (activities || []).map((act) => (
                <TableRow key={act.id} hover>
                  <TableCell style={{ whiteSpace: "nowrap", fontSize: 12 }}>{formatDate(act.createdAt)}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>
                    {act.user ? (
                      <span><strong>{act.user.name}</strong><br />
                        <span style={{ color: "#8A9096", fontSize: 11 }}>{act.user.email}</span>
                      </span>
                    ) : <span style={{ color: "#8A9096" }}>sistema</span>}
                  </TableCell>
                  <TableCell>{getChip(act.action)}</TableCell>
                  <TableCell>
                    <Tooltip title={parseDetails(act.details)} placement="top-start">
                      <span className={classes.detailsCell}>{parseDetails(act.details)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell style={{ fontSize: 11, color: "#8A9096" }}>{act.ip || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20]}
          rowsPerPage={20}
          component="div"
          count={count}
          page={page}
          onChangePage={(_, newPage) => setPage(newPage)}
        />
      </Paper>
    </MainContainer>
  );
};

export default UserActivity;
